import { getCableConsumer } from "@/lib/cable-consumer"

export type CameraLivePose = {
  pos: [number, number, number]
  yaw: number
  pitch: number
  fov: number
}

export type CameraLiveSyncOptions = {
  projectId: number
  /** Prefer scene3d stream (works for unlocated scenes). */
  scene3dId?: number | null
  /** Legacy location stream when scene3dId is absent. */
  locationId?: number | null
  clientId: string
  getPose: () => CameraLivePose | null
  onRemotePose: (pose: CameraLivePose) => void
  onRemoteSessionEnd?: () => void
  onRemotePathReady?: () => void
  /** Publish while true; call setPublisher when device camera toggles. */
  isPublisher?: boolean
  /** Publish interval ms (default ~30 Hz). */
  publishIntervalMs?: number
}

export type CameraLiveSync = {
  setPublisher: (active: boolean) => void
  notifyPathReady: () => void
  dispose: () => void
}

const CHANNEL = "Projects::LocationSceneCameraChannel"
const DEFAULT_INTERVAL_MS = 33

function isPosePayload(data: unknown): data is CameraLivePose & { type: string; client_id: string } {
  if (!data || typeof data !== "object") return false
  const d = data as Record<string, unknown>
  if (d.type !== "pose" || typeof d.client_id !== "string") return false
  if (!Array.isArray(d.pos) || d.pos.length !== 3) return false
  if (!d.pos.every((n) => typeof n === "number" && Number.isFinite(n))) return false
  if (typeof d.yaw !== "number" || !Number.isFinite(d.yaw)) return false
  if (typeof d.pitch !== "number" || !Number.isFinite(d.pitch)) return false
  if (typeof d.fov !== "number" || !Number.isFinite(d.fov)) return false
  return true
}

function isSessionEndPayload(data: unknown): data is { type: "session_end"; client_id: string } {
  if (!data || typeof data !== "object") return false
  const d = data as Record<string, unknown>
  return d.type === "session_end" && typeof d.client_id === "string" && d.client_id.length > 0
}

function isPathReadyPayload(data: unknown): data is { type: "path_ready"; client_id: string } {
  if (!data || typeof data !== "object") return false
  const d = data as Record<string, unknown>
  return d.type === "path_ready" && typeof d.client_id === "string" && d.client_id.length > 0
}

/**
 * Live camera pose sync for a 3D scene: publisher (mobile fly) → followers (desktop).
 * Prefers scene3d_id stream; falls back to location_id for legacy QR.
 */
export function createCameraLiveSync(options: CameraLiveSyncOptions): CameraLiveSync {
  const {
    projectId,
    scene3dId,
    locationId,
    clientId,
    getPose,
    onRemotePose,
    onRemoteSessionEnd,
    onRemotePathReady,
    publishIntervalMs = DEFAULT_INTERVAL_MS,
  } = options

  const channelParams: Record<string, number> = { project_id: projectId }
  if (scene3dId != null && scene3dId > 0) {
    channelParams.scene3d_id = scene3dId
  } else if (locationId != null && locationId > 0) {
    channelParams.location_id = locationId
  } else {
    return {
      setPublisher: () => {},
      notifyPathReady: () => {},
      dispose: () => {},
    }
  }

  let cancelled = false
  let confirmed = false
  let publishing = Boolean(options.isPublisher)
  let intervalId: ReturnType<typeof setInterval> | null = null

  const cable = getCableConsumer()
  const subscription = cable.subscriptions.create(
    { channel: CHANNEL, ...channelParams },
    {
      connected() {
        if (cancelled) {
          subscription.unsubscribe()
          return
        }
        confirmed = true
        syncPublishLoop()
      },
      disconnected() {
        confirmed = false
        stopPublishLoop()
      },
      rejected() {
        confirmed = false
        stopPublishLoop()
      },
      received(data: unknown) {
        if (cancelled) return
        if (isSessionEndPayload(data)) {
          if (data.client_id === clientId) return
          onRemoteSessionEnd?.()
          return
        }
        if (isPathReadyPayload(data)) {
          if (data.client_id === clientId) return
          onRemotePathReady?.()
          return
        }
        if (!isPosePayload(data)) return
        if (data.client_id === clientId) return
        onRemotePose({
          pos: data.pos as [number, number, number],
          yaw: data.yaw,
          pitch: data.pitch,
          fov: data.fov,
        })
      },
    },
  )

  function stopPublishLoop() {
    if (intervalId != null) {
      clearInterval(intervalId)
      intervalId = null
    }
  }

  function publishOnce() {
    if (!confirmed || cancelled || !publishing) return
    const pose = getPose()
    if (!pose) return
    subscription.perform("broadcast_pose", {
      client_id: clientId,
      pos: pose.pos,
      yaw: pose.yaw,
      pitch: pose.pitch,
      fov: pose.fov,
    })
  }

  function publishSessionEnd() {
    if (!confirmed || cancelled) return
    subscription.perform("broadcast_session_end", { client_id: clientId })
  }

  function notifyPathReady() {
    if (!confirmed || cancelled) return
    subscription.perform("broadcast_path_ready", { client_id: clientId })
  }

  function syncPublishLoop() {
    stopPublishLoop()
    if (!publishing || !confirmed || cancelled) return
    publishOnce()
    intervalId = setInterval(publishOnce, publishIntervalMs)
  }

  function setPublisher(active: boolean) {
    const wasPublishing = publishing
    publishing = active
    if (wasPublishing && !active) {
      publishSessionEnd()
    }
    syncPublishLoop()
  }

  function dispose() {
    stopPublishLoop()
    // session_end до cancelled: publishSessionEnd no-op при cancelled.
    if (publishing && confirmed) {
      publishing = false
      subscription.perform("broadcast_session_end", { client_id: clientId })
    }
    cancelled = true
    publishing = false
    if (confirmed) {
      confirmed = false
      subscription.unsubscribe()
    }
  }

  return { setPublisher, notifyPathReady, dispose }
}
